import { EContainerType, IModelEntityInfo } from '@/editor/types/entity';
import * as THREE from 'three';
import { EEntityType } from '../types';
import { globalStores } from '../stores';
import { ENTITY_PRIORITY } from '../constant/entity';
import { hasGeometryAndMaterial } from '@/editor/utils/object3d';

// 删除对象及其子对象的材质和几何数据
export const disposeObject = (object: THREE.Object3D) => {
  object.traverse((child) => {
    if (hasGeometryAndMaterial(child)) {
      child.geometry.dispose();
      const mtls = Array.isArray(child.material) ? child.material : [child.material];

      for (const material of mtls) {
        if (material && !material.userData.shared) {
          material.dispose();
        }
      }
    }
  });

  for (let i = object.children.length - 1; i >= 0; i--) {
    object.remove(object.children[i]);
  }
};

// 删除对象及其子对象的材质和几何数据，并从父节点中移除
export const disposeAllChildrenThenRemoveFromParent = (object: THREE.Object3D) => {
  disposeObject(object);
  if (object.parent) {
    object.parent.remove(object);
  }
};

// 不影响父节点，只删除子节点
export const disposeAllChildren = (object: THREE.Object3D) => {
  for (let i = object.children.length - 1; i >= 0; i--) {
    const child = object.children[i];
    disposeObject(child);
    object.remove(child);
  }
};

export function computeBoundingBox(object: THREE.Object3D) {
  const box = new THREE.Box3().setFromObject(object);

  return box;
}

/**
 *
 * @param box
 * @param corner
 * @returns
 */
export function get2dCorner(box: THREE.Box3, corner = 'top-right') {
  switch (corner) {
    case 'bottom-right':
      return new THREE.Vector3(box.max.x, box.min.y, 0);
    case 'bottom-left':
      return new THREE.Vector3(box.min.x, box.min.y, 0);
    case 'top-left':
      return new THREE.Vector3(box.min.x, box.max.y, 0);
    case 'top-right':
    default:
      return new THREE.Vector3(box.max.x, box.max.y, 0);
  }
}

/**
 * 获取3D模型面向摄像机的右上角点
 */
export function get3dCameraFrontRightCorner(box: THREE.Box3) {
  const camera = globalStores.editorStateStore.sceneInfo.scene3d.camera;
  const center = box.getCenter(new THREE.Vector3());
  const camToCenterDir = camera.position.clone().sub(center).normalize();

  const allCorner = [
    // new THREE.Vector3(box.min.x, box.min.y, box.min.z), // left front bottom
    // new THREE.Vector3(box.max.x, box.min.y, box.min.z), // right front bottom
    // new THREE.Vector3(box.max.x, box.max.y, box.min.z), // right back bottom
    // new THREE.Vector3(box.min.x, box.max.y, box.min.z), // left back bottom
    new THREE.Vector3(box.min.x, box.min.y, box.max.z), // left front top
    new THREE.Vector3(box.max.x, box.min.y, box.max.z), // right front top
    new THREE.Vector3(box.max.x, box.max.y, box.max.z), // right back top
    new THREE.Vector3(box.min.x, box.max.y, box.max.z) // left  back top
  ]
    .map((corner) => {
      const distance = camera.position.distanceTo(corner);
      const camToCornerDir = camera.position.clone().sub(corner).normalize();
      const corssProduct = new THREE.Vector3().crossVectors(camToCenterDir, camToCornerDir);
      const unsignedAngle = camToCenterDir.angleTo(camToCornerDir);
      const angle = corssProduct.z > 0 ? -unsignedAngle : unsignedAngle;
      return { corner, distance, angle };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 3) // 只取距离相机最近的三个角，以避免背面对角线的影响
    .sort((a, b) => b.angle - a.angle); // 找到最右边的角
  return allCorner[0].corner;
}

// 查找祖先节点，获取对应的entity
export const findEntityInfo = (object: THREE.Object3D) => {
  if (!object.visible) return null;
  let parent: THREE.Object3D | null = object;
  while (parent) {
    // 被设置成隐藏的就跳过查找
    if (!parent.visible) return null;
    const userData = parent.userData;

    switch (userData.containerType) {
      case EContainerType.noIntersectable:
        return null;
      case EContainerType.entity: {
        if (
          userData.type === EEntityType.Furniture &&
          globalStores.modelGroupStore.isModelGroupFuniture(userData.entityId)
        ) {
          const modelGroupId = globalStores.modelGroupStore.getRelateModelGroupId(userData.entityId);
          if (modelGroupId) {
            return {
              entityId: modelGroupId,
              type: EEntityType.ModelGroup,
              priority: ENTITY_PRIORITY.modelGroup
            };
          }
        } else if (
          userData.type === EEntityType.Furniture &&
          globalStores.assemblyStore.isAssemblyFuniture(userData.entityId)
        ) {
          const assemblyId = globalStores.assemblyStore.getRelateModelAssemblyId(userData.entityId);
          if (assemblyId) {
            return {
              entityId: assemblyId,
              type: EEntityType.Assembly,
              priority: ENTITY_PRIORITY.assembly
            };
          }
        }

        return {
          entityId: userData.entityId,
          type: userData.type,
          priority: userData.priority,
          containerType: userData.containerType,
          isTextInfo: userData.isTextInfo === true,
          transformType: userData.transformType
        } satisfies IModelEntityInfo;
      }
      default:
        parent = parent.parent;
    }
  }
  return null;
};

// 将mesh进行镜像
const mirrorMesh = (object: THREE.Mesh) => {
  if (!object.geometry) return;

  // 防止影响共享该几何体的其他 Mesh
  object.geometry = object.geometry.clone();
  const geometry = object.geometry;

  const matrix = new THREE.Matrix4().makeScale(-1, 1, 1);
  geometry.applyMatrix4(matrix);

  if (geometry.index) {
    // 处理索引几何体, 交换每组三个顶点中的第1个和第2个，以修正镜像后的面朝向
    const index = geometry.index.array;
    for (let i = 0; i < index.length; i += 3) {
      const tmp = index[i];
      index[i] = index[i + 1];
      index[i + 1] = tmp;
    }
    geometry.index.needsUpdate = true;
  } else {
    // 处理非索引几何体
    const pos = geometry.attributes.position.array;
    for (let i = 0; i < pos.length; i += 9) {
      // 交换第1个和第2个顶点的全部坐标(x,y,z)
      for (let j = 0; j < 3; j++) {
        const tmp = pos[i + j];
        pos[i + j] = pos[i + 3 + j];
        pos[i + 3 + j] = tmp;
      }
    }
    geometry.attributes.position.needsUpdate = true;
  }
};

export const mirrorObject = (object: THREE.Object3D) => {
  for (let i = 0; i < object.children.length; i++) {
    const child = object.children[i];
    if (child instanceof THREE.Mesh) {
      mirrorMesh(child);
    } else {
      mirrorObject(child);
    }
  }
};

// 设置模型及其子模型名称
export const setModelName = (object: THREE.Object3D, name: string) => {
  object.traverse((child) => {
    child.name = name;
  });
};

export const setModelVisible = (object: THREE.Object3D, visible: boolean) => {
  object.traverse((child) => {
    child.visible = visible;
  });
};

export const setModelRenderOrder = (object: THREE.Object3D, renderOrder: number) => {
  object.traverse((child) => {
    child.renderOrder = renderOrder;
  });
};

export function flipGeometry(geometry: THREE.BufferGeometry) {
  const index = geometry.getIndex();
  if (index) {
    const arr = index.array as Uint16Array | Uint32Array;
    for (let i = 0; i < arr.length; i += 3) {
      const tmp = arr[i + 1];
      arr[i + 1] = arr[i + 2];
      arr[i + 2] = tmp;
    }
    index.needsUpdate = true;
  } else {
    // 非索引 geometry:按 9 个分量(3 顶点)一组翻转后两个顶点
    const position = geometry.getAttribute('position');
    const pa = position.array as Float32Array;
    for (let i = 0; i < pa.length; i += 9) {
      for (let j = 0; j < 3; j++) {
        const tmp = pa[i + 3 + j];
        pa[i + 3 + j] = pa[i + 6 + j];
        pa[i + 6 + j] = tmp;
      }
    }
    position.needsUpdate = true;
  }
  const normal = geometry.getAttribute('normal');
  if (normal) {
    const na = normal.array as Float32Array;
    for (let i = 0; i < na.length; i++) na[i] = -na[i];
    normal.needsUpdate = true;
  }
}
